'use client';

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useModalA11y } from '@/hooks/use-modal-a11y';
import { IconButton } from './button';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const SIZE: Record<ModalSize, string> = {
  sm: 'max-w-[26rem]',
  md: 'max-w-[34rem]',
  lg: 'max-w-[46rem]',
  xl: 'max-w-[60rem]',
  '2xl': 'max-w-[76rem]',
};

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  size?: ModalSize;
  /** Right-aligned action row pinned under the scrollable body. */
  footer?: ReactNode;
  children: ReactNode;
}

export function Modal({ open, onClose, title, size = 'md', footer, children }: ModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  useModalA11y(open, panelRef, onClose);

  if (!open || !mounted) return null;

  return createPortal(
    <div
      data-modal-overlay="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/70 backdrop-blur-sm md:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        // Below md the dialog takes the whole screen (a phone keyboard leaves no
        // room for a floating card) and the footer stays pinned above the home bar.
        className={cn(
          'animate-fade-in flex w-full flex-col bg-zinc-900 shadow-2xl shadow-black/50',
          'max-md:safe-top max-md:safe-bottom max-md:h-full max-md:max-w-none',
          'md:corner-brand md:max-h-[85dvh] md:border md:border-zinc-700',
          SIZE[size]
        )}
      >
        <header className="flex shrink-0 items-center justify-between gap-4 px-5 pt-4 pb-3">
          <h2 id={titleId} className="truncate text-base font-semibold text-zinc-50">
            {title}
          </h2>
          <IconButton icon={X} label="Close" size="sm" onClick={onClose} />
        </header>
        <div className="thin-scroll min-h-0 flex-1 overflow-y-auto px-5 pb-4">{children}</div>
        {footer ? (
          <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-zinc-800 px-5 py-3">
            {footer}
          </footer>
        ) : null}
      </div>
    </div>,
    document.body
  );
}
